import { isNil } from 'lodash';
import { ChartNumberType, YAxisPosition } from '../interfaces';
import { useCallback } from 'react';
import { useChartContext } from '../ChartContext';
import { AxisDomain } from 'recharts/types/util/types';
import { IChartLine } from './useChartLines';
import { assertCondition } from '../../../utils/types';
import { QueryType } from '../../../utils/apollo/graphqlSchema';

const useYAxisBounds = () => {
  const context = useChartContext();
  assertCondition(context.__typename === 'BaseChartContext');
  const {
    metadata: { queryType },
    settings: {
      chartSettings: {
        chartNumberType,
        axisSettings: { yAxisSettings },
      },
    },
    displayedMeasures,
    chartData,
  } = context;
  const getYAxisDomain = useCallback(
    ({
      yAxisPosition,
      visibleLines,
    }: {
      yAxisPosition: YAxisPosition;
      /**
       * visibleLines is only needed if the chart supports negative values.
       * then we need to use visibleLine.name to check if it's in negativeValueDictionary.
       * If so, auto bounds will be set to [-auto, auto] instead of [0, auto].
       */
      visibleLines?: IChartLine[];
    }): AxisDomain => {
      const currYAxisSetting = yAxisSettings[yAxisPosition];
      if (currYAxisSetting.isTruncate) {
        return ['-auto', 'auto'];
      }

      if (
        currYAxisSetting.bounds &&
        !isNil(currYAxisSetting.bounds?.max) &&
        !isNil(currYAxisSetting.bounds?.min)
      ) {
        return [currYAxisSetting.bounds.min, currYAxisSetting.bounds.max];
      }

      if (chartNumberType === ChartNumberType.Percentage) {
        return [0, 1];
      }

      // if it's Query Type, it's possible to have negative values by adjust Formula like: A-B
      // if there is any negative value in the data, we need to set the domain to [-auto, auto]
      if (visibleLines && queryType === QueryType.Expression) {
        assertCondition(chartData.queryType === QueryType.Expression);
        const { negativeValueDictionary } = chartData;
        const columnKeysOnCurrAxis: string[] = [];

        visibleLines.forEach((line) => {
          const measure = displayedMeasures.find(
            (measure) => measure.key === line.measureKey,
          );
          if (measure?.yAxisPosition === yAxisPosition) {
            columnKeysOnCurrAxis.push(line.name);
          }
        });

        const hasNegativeValueOnCurrAxis = columnKeysOnCurrAxis.some(
          (columnKey) => negativeValueDictionary[columnKey] === true,
        );

        if (hasNegativeValueOnCurrAxis) {
          return ['-auto', 'auto'];
        }
      }
      return [0, 'auto'];
    },
    [yAxisSettings, chartNumberType, queryType, chartData, displayedMeasures],
  );

  return { getYAxisDomain };
};

export { useYAxisBounds };
